import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { PointsProvider } from './context/PointsContext';
import { AddressProvider } from './context/AddressContext';
import { WishlistProvider } from './context/WishlistContext';
import { NotificationProvider } from './context/NotificationContext';
import NotificationBell from './components/NotificationBell';
import Login from './pages/Login';
import Register from './pages/Register';
import ChangePassword from './components/ChangePassword';
import ForgotPassword from './components/ForgotPassword';
import NotificationSettings from './components/NotificationSettings';
import Home from './pages/Home';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import ProtectedRoute from './components/ProtectedRoute';
import BottomNav from './components/BottomNav';
import FloatingAIButton from './components/FloatingAIButton';
import AIChat from './pages/AIChat';
import UserMenu from './components/UserMenu';
import AdminDashboard from './pages/AdminDashboard';
import AdminChatDashboard from './components/AdminChatDashboard';
import AdminLogin from './pages/AdminLogin';
import AdminRoute from './components/AdminRoute'; 
import MyOrders from './pages/MyOrders';
import OrderTracking from './pages/OrderTracking';
import Products from './pages/Products';
import ProductDetails from './pages/ProductDetails';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Profile from './pages/Profile';
import PointsHistory from './pages/PointsHistory';
import About from './pages/About'; 
import Wishlist from './pages/Wishlist';
import PaymentCallback from './pages/PaymentCallback';
import Footer from './components/Footer';
import './App.css';
import RefundPolicy from './pages/RefundPolicy';
import FAQs from './pages/FAQs';
import TermsAndConditions from './pages/TermsAndConditions';
import Contact from './pages/Contact';
import ShippingReturns from './pages/ShippingReturns';
import { logger } from './utils/logger';

// Global loading component
const AppLoadingScreen: React.FC = () => (
  <div className="app-loading-screen">
    <div className="loading-content">
      <img
        src="/Gimbiya.png"
        alt="Gimbiya Value Store"
        className="loading-logo"
      />
      <div className="loading-spinner"></div>
      <p>Loading your shopping experience...</p>
    </div>
  </div>
);

function AppContent() {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id;
  const location = useLocation();
  const navigate = useNavigate();
  const [isAppReady, setIsAppReady] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const isAdmin: boolean = user?.isAdmin === true || user?.role === 'admin';
  const showBottomNav: boolean =
    location.pathname !== '/login' &&
    location.pathname !== '/register' &&
    location.pathname !== '/ai-chat' &&
    !location.pathname.startsWith('/track-order/')
  ;
 
  // Handle Paystack App Link callbacks on Android.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    let active = true;
    let listenerHandle: { remove: () => Promise<void> } | undefined;
    let lastHandledUrl = '';

    const handleCallbackUrl = (rawUrl: string) => {
      if (!active || !rawUrl || rawUrl === lastHandledUrl) {
        return;
      }

      try {
        const callbackUrl = new URL(rawUrl);

        if (
          callbackUrl.protocol !== 'https:' ||
          callbackUrl.hostname !== 'gimbiyastore.com' ||
          callbackUrl.pathname !== '/payment/callback'
        ) {
          return;
        }

        lastHandledUrl = rawUrl;

        logger.success('Paystack callback received by GVS');

        navigate(
          `/payment/callback${callbackUrl.search}`,
          { replace: true }
        );
      } catch (error) {
        console.error('Invalid payment callback URL:', error);
      }
    };

    const registerListener = async () => {
      try {
        listenerHandle = await CapacitorApp.addListener(
          'appUrlOpen',
          ({ url }) => handleCallbackUrl(url)
        );

        const launch = await CapacitorApp.getLaunchUrl();

        if (launch?.url) {
          handleCallbackUrl(launch.url);
        }
      } catch (error) {
        console.error('Unable to register payment callback listener:', error);
      }
    };

    registerListener();

    return () => {
      active = false;
      listenerHandle?.remove();
    };
  }, [navigate]);
  // Initialize app and hide splash
  useEffect(() => {
    const initializeApp = async () => {
      if (!authLoading) {
        await new Promise(resolve => setTimeout(resolve, 200));
        
        if (Capacitor.isNativePlatform()) {
          try {
            await SplashScreen.hide();
            logger.success('Splash screen hidden');
          } catch (error) {
            console.error('Error hiding splash:', error);
          }
        }
        
        setIsAppReady(true);
        setTimeout(() => setIsInitialLoad(false), 100);
      }
    };
    
    initializeApp();
  }, [authLoading]);

    // Add this useEffect to App.tsx to detect Capacitor
  useEffect(() => {
    const isCapacitor = !!(window as any).Capacitor;
    if (isCapacitor) {
      document.body.classList.add('capacitor');
      logger.success('Capacitor detected - applying mobile optimizations');
    }
  }, []);

  
  // Show loading screen while app is initializing
  if (isInitialLoad || !isAppReady) {
    return <AppLoadingScreen />;
  } 

  return (
    <NotificationProvider userId={userId} isAdmin={isAdmin}>
      <div className="app">
        <>
          {location.pathname !== '/' && (
            <div className="top-bar">
              <div className="logo" onClick={() => navigate('/')}>
                <img src="/Gimbiya.png" alt="Gimbiya" className="logo-image" />
              </div>
              <NotificationBell isAdmin={isAdmin} />
              <UserMenu />
            </div>
          )}
          
          <div className="main-content">
            <Routes>
              {/* =========================================================
                  PUBLIC AUTHENTICATION ROUTES
                  ========================================================= */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />


              {/* =========================================================
                  PUBLIC SHOPPING ROUTES
                  Guests can browse and use the cart.
                  ========================================================= */}
              <Route path="/" element={<Home />} />
              <Route path="/products" element={<Products />} />
              <Route path="/product/:id" element={<ProductDetails />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/faqs" element={<FAQs />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/shipping-returns" element={<ShippingReturns />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/about" element={<About />} />


              {/* =========================================================
                  PROTECTED CUSTOMER ROUTES
                  Authentication required.
                  ========================================================= */}
              <Route
                path="/checkout"
                element={
                  <ProtectedRoute>
                    <Checkout />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/payment/callback"
                element={
                  <ProtectedRoute>
                    <PaymentCallback />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/my-orders"
                element={
                  <ProtectedRoute>
                    <MyOrders />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/track-order/:orderId"
                element={
                  <ProtectedRoute>
                    <OrderTracking />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/wishlist"
                element={
                  <ProtectedRoute>
                    <Wishlist />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/ai-chat"
                element={<AIChat />}
              />

              <Route
                path="/points-history"
                element={
                  <ProtectedRoute>
                    <PointsHistory />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/change-password"
                element={
                  <ProtectedRoute>
                    <ChangePassword />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <NotificationSettings />
                  </ProtectedRoute>
                }
              />


              {/* =========================================================
                  ADMIN ROUTES
                  ========================================================= */}
              <Route path="/admin-login" element={<AdminLogin />} />

              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/chats"
                element={
                  <AdminRoute>
                    <AdminChatDashboard />
                  </AdminRoute>
                }
              />
            </Routes>
          </div>

          <Footer />

          {showBottomNav && (
            <BottomNav isAdmin={isAdmin} />
          )}

          {/* Floating AI Button - No onClick needed, it handles navigation internally */}
          {location.pathname !== '/ai-chat' && (
            <FloatingAIButton />
          )}
        </>
        
        
      </div>
    </NotificationProvider>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AddressProvider>
          <CartProvider>
            <PointsProvider>
              <WishlistProvider>
                <AppContent />
              </WishlistProvider>
            </PointsProvider>
          </CartProvider>
        </AddressProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
